/**
 * HistoricalImportDialog — the Slice 2 upload surface.
 *
 * Despite the name, this is rendered as a PAGE (not a modal) by
 * the OnboardSetupImport route. The name follows the original
 * FirstLoginJunction comment that referenced
 * `<HistoricalImportDialog />` as the eventual replacement for
 * the internal placeholder.
 *
 * UX rules:
 *   - Only Generic CSV is selectable. Other vendor formats are
 *     listed as DISABLED placeholders with a "Coming in a future
 *     update" tag so the customer can see the roadmap without
 *     being able to click a fake path.
 *   - File picker AND paste box, mutually exclusive. The paste
 *     box exists for shops whose DMS exports are emailed as text
 *     rather than as a .csv file (common with smaller systems).
 *   - Optional browser-side preview using the same parser the
 *     edge function will run — gives the user a sample-counts
 *     readout BEFORE pressing Upload. The Upload button is
 *     enabled even without preview; preview is informational.
 *   - On Upload: calls upload-import-csv via supabase.functions
 *     and navigates to /onboard/setup/import/:runId/preview on
 *     success. Failure surfaces inline; no navigation.
 *
 * NEVER:
 *   - Writes to live shop config.
 *   - Writes to permanent shop_history_* (only upload-import-csv
 *     can, server-side).
 *   - Skips the importEnabledForInternal gate; the route layer
 *     enforces it, this component additionally guards itself.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, AlertCircle, Loader2, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import { parseGenericCsv } from '@/lib/shopHistoryImport';
import { toast } from '@/hooks/use-toast';

interface VendorOption {
  key: 'generic-csv' | 'napa-tracs' | 'vast' | 'tekmetric' | 'shop-ware';
  label: string;
  description: string;
  /** True iff selectable in Slice 2.5. */
  enabled: boolean;
}

const VENDOR_OPTIONS: VendorOption[] = [
  {
    key: 'generic-csv',
    label: 'Generic CSV',
    description: 'A flat CSV export from any DMS. Use this for unknown or unlisted systems.',
    enabled: true,
  },
  {
    key: 'napa-tracs',
    label: 'NAPA TRACS',
    description: 'Coming in a future update.',
    enabled: false,
  },
  {
    key: 'vast',
    label: 'VAST',
    description: 'Coming in a future update.',
    enabled: false,
  },
  {
    key: 'tekmetric',
    label: 'Tekmetric',
    description: 'Coming in a future update.',
    enabled: false,
  },
  {
    key: 'shop-ware',
    label: 'Shop-Ware',
    description: 'Coming in a future update.',
    enabled: false,
  },
];

interface PreviewCounts {
  customers: number;
  vehicles: number;
  repairOrders: number;
  jobs: number;
  warnings: number;
  missingRequiredFields: string[];
}

function previewCsv(text: string): PreviewCounts {
  const parsed = parseGenericCsv(text);
  return {
    customers: parsed.customers.length,
    vehicles: parsed.vehicles.length,
    repairOrders: parsed.repairOrders.length,
    jobs: parsed.jobs.length,
    warnings: parsed.warnings.length,
    missingRequiredFields: parsed.missingRequiredFields,
  };
}

export function HistoricalImportDialog() {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const importEnabled = useFeatureFlag('importEnabledForInternal');

  const [selectedVendor] = useState<VendorOption['key']>('generic-csv');
  const [csvText, setCsvText] = useState<string>('');
  const [filename, setFilename] = useState<string | null>(null);
  const [preview, setPreview] = useState<PreviewCounts | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Defensive flag re-check. The route layer also enforces this,
  // but a stale render or direct nav with the flag flipped would
  // otherwise show the UI.
  if (!importEnabled) {
    return (
      <Card data-testid="import-gate-unavailable">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5" /> Import is not enabled
          </CardTitle>
          <CardDescription>
            The historical-import workflow is internal-only right now. Return to setup and use Manual Quick Setup.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  const handleFileChosen = async (file: File) => {
    setUploadError(null);
    setPreviewError(null);
    setPreview(null);
    setFilename(file.name);
    try {
      const text = await file.text();
      setCsvText(text);
    } catch (e) {
      setPreviewError(`Could not read file: ${(e as Error).message ?? String(e)}`);
      setCsvText('');
    }
  };

  const handlePreview = () => {
    setPreviewError(null);
    if (!csvText.trim()) {
      setPreviewError('Paste a CSV or choose a file first.');
      return;
    }
    try {
      const result = previewCsv(csvText);
      setPreview(result);
    } catch (e) {
      setPreviewError(`Preview failed: ${(e as Error).message ?? String(e)}`);
    }
  };

  const handleUpload = async () => {
    setUploadError(null);
    if (!activeShop) {
      setUploadError('No active shop. Refresh and try again.');
      return;
    }
    if (!csvText.trim()) {
      setUploadError('Paste a CSV or choose a file first.');
      return;
    }
    setIsUploading(true);
    try {
      const { data, error } = await supabase.functions.invoke('upload-import-csv', {
        body: {
          shop_id: activeShop.id,
          source_format: 'generic-csv',
          csv_text: csvText,
          source_filename: filename,
          time_zone: (activeShop as { time_zone?: string }).time_zone ?? null,
        },
      });
      if (error) {
        setUploadError(error.message ?? 'Upload failed');
        return;
      }
      // The edge function returns { ok, code, error, ... } for failures
      // alongside the standard supabase invoke envelope. Surface both.
      const body = (data ?? {}) as {
        ok?: boolean;
        code?: string;
        error?: string;
        import_run_id?: string;
        missing_required_fields?: string[];
      };
      if (body.ok === false || !body.import_run_id) {
        const msg = body.error ?? 'Upload failed';
        const missing = body.missing_required_fields?.length
          ? ` Missing columns: ${body.missing_required_fields.join(', ')}.`
          : '';
        setUploadError(`${msg}${missing}`);
        return;
      }
      toast({
        title: 'Upload received',
        description: 'Your import is being prepared for promotion.',
      });
      navigate(`/onboard/setup/import/${body.import_run_id}/preview`);
    } catch (e) {
      setUploadError(`Unexpected upload error: ${(e as Error).message ?? String(e)}`);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div
      className="mx-auto w-full max-w-3xl space-y-6 p-4"
      data-testid="historical-import-dialog"
    >
      <div className="space-y-1">
        <h1 className="text-2xl font-bold">Import Shop History</h1>
        <p className="text-sm text-muted-foreground">
          Upload an export from your previous system. OptiBay will analyze it and prefill your setup.
        </p>
      </div>

      {/* Vendor picker. Only Generic CSV is clickable. */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Source system</CardTitle>
          <CardDescription>Pick the system your export came from.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {VENDOR_OPTIONS.map((v) => {
            const isSelected = selectedVendor === v.key;
            return (
              <div
                key={v.key}
                data-testid={`vendor-option-${v.key}`}
                data-enabled={v.enabled}
                aria-disabled={!v.enabled}
                className={[
                  'flex items-center justify-between rounded border p-3',
                  isSelected ? 'border-primary bg-primary/5' : 'border-muted',
                  v.enabled ? '' : 'opacity-60',
                ].join(' ')}
              >
                <div>
                  <div className="text-sm font-medium">{v.label}</div>
                  <div className="text-xs text-muted-foreground">{v.description}</div>
                </div>
                {!v.enabled && (
                  <Badge variant="secondary" data-testid={`vendor-future-${v.key}`}>
                    Future
                  </Badge>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* File + paste */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Provide your CSV</CardTitle>
          <CardDescription>Choose a file or paste CSV text below.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="csv-file-input">CSV file</Label>
            <input
              id="csv-file-input"
              data-testid="csv-file-input"
              type="file"
              accept=".csv,text/csv"
              className="mt-1 block w-full text-sm"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFileChosen(file);
              }}
            />
            {filename && (
              <div className="mt-1 text-xs text-muted-foreground" data-testid="csv-filename">
                {filename}
              </div>
            )}
          </div>
          <div>
            <Label htmlFor="csv-paste-input">Or paste CSV text</Label>
            <Textarea
              id="csv-paste-input"
              data-testid="csv-paste-input"
              rows={6}
              placeholder="RO #,Opened,Customer #,…"
              value={csvText}
              onChange={(e) => {
                setCsvText(e.target.value);
                setPreview(null);
                setPreviewError(null);
              }}
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={handlePreview}
              disabled={isUploading}
              data-testid="csv-preview-button"
            >
              <FileText className="mr-2 h-4 w-4" /> Preview counts
            </Button>
            <Button
              type="button"
              onClick={handleUpload}
              disabled={isUploading || !csvText.trim()}
              data-testid="csv-upload-button"
            >
              {isUploading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Upload className="mr-2 h-4 w-4" />
              )}
              Upload and continue
            </Button>
          </div>
          {previewError && (
            <div
              className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
              data-testid="preview-error"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{previewError}</span>
            </div>
          )}
          {uploadError && (
            <div
              className="flex items-start gap-2 rounded border border-destructive/40 bg-destructive/5 p-2 text-xs text-destructive"
              data-testid="upload-error"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview readout */}
      {preview && (
        <Card data-testid="preview-counts">
          <CardHeader>
            <CardTitle className="text-base">Preview</CardTitle>
            <CardDescription>
              These counts are computed locally — your file has not been uploaded yet.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-2 text-sm md:grid-cols-4">
            <Stat label="Customers" value={preview.customers} testid="preview-customers" />
            <Stat label="Vehicles" value={preview.vehicles} testid="preview-vehicles" />
            <Stat label="Repair orders" value={preview.repairOrders} testid="preview-ros" />
            <Stat label="Jobs" value={preview.jobs} testid="preview-jobs" />
            {preview.warnings > 0 && (
              <Stat
                label="Per-row warnings"
                value={preview.warnings}
                testid="preview-warnings"
              />
            )}
            {preview.missingRequiredFields.length > 0 && (
              <div
                className="col-span-2 rounded border border-amber-500/40 bg-amber-500/5 p-2 text-xs text-amber-700 md:col-span-4"
                data-testid="preview-missing-required"
              >
                Missing required columns: {preview.missingRequiredFields.join(', ')}. Upload will fail
                until those are remapped.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function Stat({ label, value, testid }: { label: string; value: number; testid: string }) {
  return (
    <div className="rounded border bg-muted/40 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold" data-testid={testid}>
        {value}
      </div>
    </div>
  );
}
